import {
  AidAdviceDetail,
  CropAndCropAid,
  CropFieldAdvice,
  CropFieldAdviceDetail,
  CropFieldAdviceGroup,
  LegalRestriction,
  TreatmentDetail,
  TreatmentType,
} from '@next/arable-lib/advice';
import { getArableAdviceCropFields } from '@next/arable-lib/crop-field';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { createSelector } from '@ngrx/store';
import { CropAid, GetAutomaticAdviceResponse, GetCompanySettingsResponse, GroupedTreatments, SalesOrderPeriod } from 'src/app/shared/models';
import { selectLegalRestrictions, selectSelectedBlightCropFieldIds } from 'src/app/store/selectors';

import { selectAdvicesState } from './feature';

export const selectCropVarietiesByCropId = createSelector(
  selectAdvicesState,
  (store) => store.manualCrops?.cropVarieties,
);

export const selectAutomaticAdvice = createSelector(
  selectAdvicesState,
  (store): GetAutomaticAdviceResponse | undefined => store?.automaticAdvice || undefined,
);

export const selectCropFieldAdvices = createSelector(
  selectAdvicesState,
  (store): CropFieldAdvice[] => store?.cropFieldAdvices || [],
);

export const selectCompanySettings = createSelector(
  selectAdvicesState,
  (store): GetCompanySettingsResponse | undefined => store?.companySettings || undefined,
);

export const selectIsAutomaticAdviceCustomer = createSelector(
  selectAdvicesState,
  (store): boolean | undefined => store?.companySettings?.automaticAdvice || undefined,
);

export const selectHasAnyFinalizedOrderLines = createSelector(
  selectAdvicesState,
  (store) => store.hasAnyFinalizedOrderLines ?? false,
);

export const selectCustomerOrderPlan = createSelector(
  selectAdvicesState,
  (app) => app.manualCrops.customerOrderPlan || [],
);

export const selectOrderLines = createSelector(selectAdvicesState, (app) => app.manualCrops.orderLines || []);

export const selectGBMOrderLines = createSelector(selectAdvicesState, (app) => app.manualCrops.gbmOrderLines || []);

export const selectFertilizerOrderLines = createSelector(
  selectAdvicesState,
  (app) => app.manualCrops.fertilizerOrderLines || [],
);

export const selectFertilizerOrderPlan = createSelector(
  selectAdvicesState,
  (app) => app.manualCrops.fertilizerOrderPlan,
);

export const selectSalesOrderPeriodsFromStore = createSelector(
  selectAdvicesState,
  (app) => app.manualCrops?.salesOrderPeriods || [],
);

export const selectAllOrderLines = createSelector(
  selectGBMOrderLines,
  selectFertilizerOrderLines,
  (gbmOrderLines, fertilizerOrderLines) => [...gbmOrderLines, ...fertilizerOrderLines],
);

export const selectLastUsedProvince = createSelector(getArableAdviceCropFields, (cropFields) => {
  const cropFieldArray = Array.isArray(cropFields) ? cropFields : [];
  const lastProvince = cropFieldArray.find((field) => field?.province)?.province;
  return lastProvince || '';
});

export const selectPackages = createSelector(selectAdvicesState, (app) => app.manualCrops.packages);
export const selectManualCropAids = createSelector(selectAdvicesState, (app) => app.manualCrops.cropAids);
export const selectCropAidPackages = createSelector(selectAdvicesState, (app) => app.manualCrops.cropAidPackages);

// The user is eligible if the company settings do not have automatic advice enabled,
// if there are any finalized order lines, or if the user has at least the advisor role for the My Arable application.
export const selectIsUserEligibleToViewCropFieldAdvices = createSelector(
  selectCompanySettings,
  selectHasAnyFinalizedOrderLines,
  AuthorizationSelectors.hasAtLeastAdvisorRoleForApplicationKey('My Arable'),
  (companySettings, hasAnyFinalizedOrderLines, hasAtLeastAdvisorRoleForMyArable) =>
    companySettings?.automaticAdvice !== true || hasAnyFinalizedOrderLines || hasAtLeastAdvisorRoleForMyArable,
);

export const selectCropAndCropAids = createSelector(selectCropFieldAdvices, (cropFieldAdvices) => {
  const uniquePairs = new Map<string, CropAndCropAid>();

  cropFieldAdvices.forEach((cropFieldAdvice) => {
    const cropId = cropFieldAdvice.crop.id;

    cropFieldAdvice.treatments
      .flatMap((treatment) => treatment.aidAdvices)
      .forEach((aidAdvice) => {
        const cropAidId = aidAdvice.cropAidId;
        uniquePairs.set(`${cropId}-${cropAidId}`, { cropId, cropAidId });
      });
  });

  return Array.from(uniquePairs.values());
});

export const selectCropFieldAdviceIds = createSelector(selectCropFieldAdvices, (cropFieldAdvices) =>
  cropFieldAdvices.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId),
);

export const selectCropFieldAdvicesDetail = createSelector(
  selectCropFieldAdvices,
  selectManualCropAids,
  selectSalesOrderPeriodsFromStore,
  (
    cropFieldAdvices: CropFieldAdvice[],
    cropAids: CropAid[],
    salesOrderPeriods: SalesOrderPeriod[],
  ): CropFieldAdviceDetail[] => {
    return cropFieldAdvices.map(
      (cropFieldAdvice) =>
        ({
          ...cropFieldAdvice,
          treatments: cropFieldAdvice.treatments.map(
            (treatment) =>
              ({
                ...treatment,
                period: salesOrderPeriods.find((period) => period.id === treatment.periodId),
                aidAdvices: treatment.aidAdvices.map(
                  (aidAdvice) =>
                    ({
                      ...aidAdvice,
                      cropAid: cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId),
                    }) as unknown as AidAdviceDetail,
                ),
              }) as unknown as TreatmentDetail,
          ),
        }) as unknown as CropFieldAdviceDetail,
    );
  }
  ,
);

export const selectUniqueAdvices = ({ treatmentType }: { treatmentType: TreatmentType }) =>
  createSelector(
    selectCropFieldAdvicesDetail,
    selectCropFieldAdviceIds,
    selectLegalRestrictions,
    (
      cropFieldAdvices: CropFieldAdviceDetail[],
      ids: number[],
      legalRestrictions: LegalRestriction[],
    ): CropFieldAdviceGroup[] =>
       Object.values(
        cropFieldAdvices
          .filter((cropFieldAdvice) => ids.indexOf(cropFieldAdvice.cropFieldId) !== -1)
          .map((cropFieldAdvice) => ({
            cropFieldAdvice: {
              ...cropFieldAdvice,
              treatments: cropFieldAdvice.treatments.map((treatment) => ({
                ...treatment,
                aidAdvices: treatment.aidAdvices
                  .map((aidAdvice) => {
                    const legalRestriction = legalRestrictions.find(
                      (restriction) =>
                        restriction.cropAidId === aidAdvice.cropAidId && restriction.cropId === cropFieldAdvice.crop.id,
                    );
                    return {
                      ...aidAdvice,
                      legalRestriction,
                    };
                  })
                  .filter((aidAdvice): aidAdvice is NonNullable<typeof aidAdvice> => aidAdvice !== null),
              })),
            },
            compareBy: JSON.stringify({
              cropId: cropFieldAdvice.crop.id,
              treatments: cropFieldAdvice.treatments
                .filter((treatment) => treatment.treatmentType === treatmentType)
                .map(
                  ({
                    aidAdvices,
                    moment,
                    name,
                    objective,
                    periodId,
                    pointOfAttention,
                    shouldUseAreaInHectaresManual,
                  }) => ({
                    aidAdvices,
                    moment,
                    name,
                    objective,
                    periodId,
                    pointOfAttention,
                    shouldUseAreaInHectaresManual,
                  }),
                ),
            }),
          }))
          .reduce(
            (acc, curr) => {
              const existingGroup = acc[curr.compareBy];
              const newGroup: CropFieldAdviceGroup = {
                cropFieldAdvices: existingGroup
                  ? [...existingGroup.cropFieldAdvices, curr.cropFieldAdvice]
                  : [curr.cropFieldAdvice],
                cropId: curr.cropFieldAdvice.crop.id,
                cropName: curr.cropFieldAdvice.crop.name,
                cropVarietyName: curr.cropFieldAdvice?.cropVariety?.name,
                totalHectares: (existingGroup ? existingGroup.totalHectares : 0) + curr.cropFieldAdvice.areaInHectares,
                treatments: curr.cropFieldAdvice.treatments
                  .filter((treatment) => treatment.treatmentType === treatmentType)
                  .map((treatment) => ({
                    ...treatment,
                    aidAdvices: treatment.aidAdvices
                      .map((aidAdvice) => {
                        const legalRestriction = legalRestrictions.find(
                          (restriction) =>
                            restriction.cropAidId === aidAdvice.cropAidId &&
                            restriction.cropId === curr.cropFieldAdvice.crop.id,
                        );

                        return {
                          ...aidAdvice,
                          legalRestriction,
                        };
                      }),
                  })),
                compareBy: curr.compareBy,
              };

              return {
                ...acc,
                [curr.compareBy]: newGroup,
              };
            },
            {} as Record<string, CropFieldAdviceGroup>,
          ),
      )

    ,
  );

export const selectUniqueAdvicesWithTreatments = (filters: { treatmentType: TreatmentType }) =>
  createSelector(selectUniqueAdvices(filters), (advices) => advices.filter((advice) => advice.treatments.length > 0));

export const selectUniqueAdvicesWithoutTreatments = (filters: { treatmentType: TreatmentType }) =>
  createSelector(selectUniqueAdvices(filters), (advices) => advices.filter((advice) => advice.treatments.length === 0));

export const selectGBMTreatmentsGroupedByCropField = createSelector(
  selectCropFieldAdvices,
  selectSelectedBlightCropFieldIds,
  (cropFieldAdvices: CropFieldAdvice[], selectedCropFieldIds: number[]): GroupedTreatments[] => {
    if (!cropFieldAdvices || !selectedCropFieldIds) {
      return [];
    }

    const selectedCropFields = cropFieldAdvices.filter((cropField) =>
      selectedCropFieldIds.includes(cropField.cropFieldId),
    );
    const groupedTreatments = selectedCropFields
      .map((cropField) => {
        const gbmTreatments = cropField.treatments.filter((treatment) => treatment.treatmentType === TreatmentType.GBM);

        return {
          cropFieldId: cropField.cropFieldId,
          cropFieldName: cropField.name,
          treatments: gbmTreatments,
        };
      })
      .filter((group) => group.treatments.length > 0);

    return groupedTreatments;
  },
);
